import { dayjs } from "@/core/utils/date";
import { DEFAULT_CURRENCY } from "@/profitability/currency";
import {
  compareMissingLast,
  isMissing,
  MISSING,
} from "@/profitability/missing";
import type { ReportColumn, ReportValue } from "@/profitability/types";
import { formatSummaryValue } from "@/profitability/utils";

/** Blanks last, numbers by size, text the way people read it. */
export const compareValues = (
  a: string | number | null,
  b: string | number | null,
  /** Flips the order of the values that are there; blanks stay last. */
  direction: "asc" | "desc" = "asc",
) =>
  compareMissingLast(a, b, direction, (left, right) => {
    if (left === right) return 0;
    if (typeof left === "number" && typeof right === "number")
      return left - right;
    return String(left).localeCompare(String(right), undefined, {
      numeric: true,
    });
  });

export const isNumeric = (column: ReportColumn) =>
  column.type === "number" ||
  column.type === "currency" ||
  column.type === "percent";

/** A cell's value as the table shows it: money without cents, rates to one place. */
export const formatCell = (
  value: ReportValue | undefined,
  column: ReportColumn,
  /** The report's currency (`useReportCurrency`). */
  currency: string = DEFAULT_CURRENCY,
) => {
  // A figure with no value is missing, never zero (FIN-004): render the
  // result through `showValue` so it reads as "Missing" to a screen reader.
  if (isNumeric(column) && isMissing(value)) return MISSING;
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (column.type === "date" && typeof value === "string")
    return dayjs(value).format("MMM D");
  return formatSummaryValue({ value, type: column.type }, currency);
};

export const sortableValue = (value: ReportValue | undefined) =>
  value === undefined || isMissing(value)
    ? null
    : typeof value === "boolean"
      ? Number(value)
      : value;
